import {
  ActivityType,
  activityTypeColorRecord,
  CalculatedAreaActivityType,
} from '@shared/models/design/activities/activity-type.model';
import {
  Activity,
  ActivityMap,
  ActivityPropertyMap,
} from '@shared/models/design/activities/activity.model';
import { guards } from '../typeguards.helpers';
import { labelFromValue } from '../string.helpers';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { getGfaFromStoreys, getPerimeterFromStoreys } from './shape.helpers';
import {
  getSharedLengthForPerimeter,
  getStoreyEnvelopeMetricsById,
} from './envelope.helpers';
import { ActivityReference, Storey } from '@shared/models/design/storey.model';
import {
  defaultResidentialProperties,
  defaultSchoolProperties,
  defaultStairwellsProperties,
} from '@shared/models/design/activities/properties';
import { CALCULATIONS } from '@shared/constants/calculations.constants';
import {
  ActivityRefSelector,
  getActivityReference,
} from './activity-ref.helpers';
import { getStairwellsPropertiesWithDefaults } from './expressions/activity-expression-variables.helpers';
import { activityRefGuards as activityGuards } from './guards/activity-guards.helpers';
import { expressionVariablesConstants } from '@shared/constants/expression-variables.constants';
import { getAllStoreys } from './storey.helpers';

/**
 * Pre-computed storey-level values for activity area calculations.
 * Ensures each intermediate value is calculated exactly once per storey.
 */
export type StoreyCalculationContext = {
  gfa: number;
  perimeter: number;
  gfaFacades: number;
  gfaInstallations: number;
  gfaStairwells: number;
  gfaInnerwalls: number;
  residentialProperties: ActivityPropertyMap[ActivityType.Residential];
  stairwellsProperties: ActivityPropertyMap[ActivityType.Stairwells];
  /** Per-stairwell-ref computed GFA used for distributing total stairwell area */
  stairwellPerRefGfa: Map<ActivityReference['id'], number>;
};

/**
 * Get the default properties for an activity type.
 * Henrik says we should follow the established standards from Boverket and old implementations.
 */
export const getDefaultActivityProperties = <T extends ActivityType>(
  type: T,
): ActivityPropertyMap[T] => {
  switch (type) {
    case ActivityType.Residential:
      return defaultResidentialProperties as ActivityPropertyMap[T];
    case ActivityType.School:
      return defaultSchoolProperties as ActivityPropertyMap[T];
    case ActivityType.Stairwells:
      return defaultStairwellsProperties as ActivityPropertyMap[T];
    default:
      return {} as ActivityPropertyMap[T];
  }
};

/**
 * Merge persisted activity properties with type defaults (e.g. legacy residential without program_type).
 */
export const getActivityPropertiesWithDefaults = <T extends ActivityType>(
  type: T,
  properties?: ActivityPropertyMap[T],
): ActivityPropertyMap[T] => ({
  ...getDefaultActivityProperties(type),
  ...(properties ?? {}),
});

/**
 * Get the ActivityType from an Activity or ActivityType
 * @param activityOrType Activity or ActivityType
 * @returns ActivityType, will fallback to ActivityType.Other if no activity or type is provided
 */
export const getActivityType = (
  activityOrType?: Activity | ActivityType | ActivityReference,
): ActivityType => {
  if (guards.is.object(activityOrType)) {
    return activityOrType.type;
  }
  return activityOrType ?? ActivityType.Other;
};

/**
 * Get the color for an ActivityType or Activity
 * @param typeOrActivity ActivityType or Activity
 * @returns Color string
 */
export const getActivityTypeColor = (
  typeOrActivity: ActivityType | Activity | ActivityReference,
): string => {
  return activityTypeColorRecord[getActivityType(typeOrActivity)];
};

/**
 * Get the label for an ActivityType or Activity
 * @param typeOrActivity ActivityType or Activity
 * @returns Label string
 */
export const getActivityTypeLabel = (
  typeOrActivity: ActivityType | Activity | ActivityReference,
): string => {
  // If the activity-ref has a name, use it
  if (
    guards.is.object(typeOrActivity) &&
    'name' in typeOrActivity &&
    guards.is.string(typeOrActivity.name) &&
    typeOrActivity.name.length > 0
  ) {
    return typeOrActivity.name;
  }
  const type = getActivityType(typeOrActivity);
  switch (type) {
    case ActivityType.Industry:
      return 'Logistics & Industry';
    case ActivityType.Innerwalls:
      return 'Inner walls';
    default:
      return labelFromValue(type);
  }
};

/**
 * Get a record of calculated activity areas on a storey (by type).
 * Uses {@link calculateStoreyContext} so each intermediate value is computed once.
 * @param design Building design
 * @param storey For which storey to calculate
 * @returns Record of calculated activity areas by type
 */
export const getCalculatedActivityAreaRecordFromStorey = (
  design: BuildingDesign,
  storey: Storey,
): Record<CalculatedAreaActivityType, number> => {
  const ctx = calculateStoreyContext(design, storey);
  return {
    [ActivityType.Facades]: ctx.gfaFacades,
    [ActivityType.Installations]: ctx.gfaInstallations,
    [ActivityType.Stairwells]: ctx.gfaStairwells,
    [ActivityType.Innerwalls]: ctx.gfaInnerwalls,
  };
};

/**
 * Get the total calculated activity area on a storey (area controlled by variables).
 * @param design Building design
 * @param storey For which storey to calculate
 * @returns Total calculated activity area on the storey
 */
export const getCalculatedActivityAreaOnStorey = (
  design: BuildingDesign,
  storey: Storey,
): number => {
  const ctx = calculateStoreyContext(design, storey);
  return (
    ctx.gfaFacades +
    ctx.gfaInstallations +
    ctx.gfaStairwells +
    ctx.gfaInnerwalls
  );
};

/**
 * Get area for an activity reference.
 * Some are calculated based on properties, others by a relative share.
 * Accepts an optional pre-computed {@link StoreyCalculationContext} to avoid redundant work.
 * @param design Building design
 * @param storey Storey containing the activity reference
 * @param refSelector Selector for the activity reference
 * @param ctx Optional pre-computed context (created if not provided)
 */
export const getActivityReferenceArea = (
  design: BuildingDesign,
  storey: Storey,
  refSelector: ActivityRefSelector,
  ctx?: StoreyCalculationContext,
): number => {
  const activityRef = getActivityReference(storey, refSelector);
  if (!activityRef) return 0;

  const context = ctx ?? calculateStoreyContext(design, storey);
  if (context.gfa <= 0) return 0;

  const { share, type } = activityRef;

  switch (type) {
    case ActivityType.Facades:
      return context.gfaFacades;
    case ActivityType.Installations:
      return context.gfaInstallations;
    case ActivityType.Stairwells: {
      // Distribute total stairwell GFA by per-ref weight
      const totalPerRefGfa = [...context.stairwellPerRefGfa.values()].reduce(
        (a, b) => a + b,
        0,
      );
      const refGfa = context.stairwellPerRefGfa.get(activityRef.id) ?? 0;
      const stairwellShare =
        totalPerRefGfa > 0
          ? refGfa / totalPerRefGfa
          : 1 / Math.max(1, context.stairwellPerRefGfa.size);
      return context.gfaStairwells * stairwellShare;
    }
    case ActivityType.Innerwalls:
      return context.gfaInnerwalls;
    default: {
      if (activityGuards.is.calculatedAreaActivityType(type)) {
        throw new Error('Calculated activity area type found in default case');
      }
      const calculatedArea =
        context.gfaFacades +
        context.gfaInstallations +
        context.gfaStairwells +
        context.gfaInnerwalls;
      return (share ?? 0) * (context.gfa - calculatedArea);
    }
  }
};

/**
 * Input type for activities.
 */
type ActivitiesInput = BuildingDesign | Activity[];

/**
 * Get activities from design or array of activities.
 * Made to make it easier to test and use activities in different contexts.
 * @param design Building design or array of activities
 * @returns Array of activities
 */
export const getActivities = (
  activitiesOrDesign: ActivitiesInput,
): Activity[] => {
  if (guards.is.array(activitiesOrDesign)) {
    return activitiesOrDesign;
  }
  return activitiesOrDesign.activities;
};

/**
 * Drop {@link BuildingDesign.activities} that no storey still references.
 * Used after a scoped demolish so a removed building's programs cannot
 * re-seed a later footprint via sibling activity copy.
 */
export const pruneUnreferencedDesignActivities = (
  design: BuildingDesign,
): BuildingDesign => {
  const referencedIds = new Set(
    getAllStoreys(design).flatMap((storey) =>
      storey.activities.map((ref) => ref.activity_id),
    ),
  );
  const activities = design.activities.filter((activity) =>
    referencedIds.has(activity.id),
  );
  if (activities.length === design.activities.length) return design;
  return { ...design, activities };
};

/**
 * Get activity by type from design.
 */
export const getActivityByType = <Type extends ActivityType>(
  activitiesOrDesign: ActivitiesInput,
  type: Type,
): ActivityMap[Type] | undefined =>
  getActivities(activitiesOrDesign).find((a) => a.type === type) as
    ActivityMap[Type] | undefined;

export const getActivityByReference = (
  activitiesOrDesign: ActivitiesInput,
  reference: ActivityReference,
): Activity | undefined =>
  getActivities(activitiesOrDesign).find((a) => a.id === reference.activity_id);

// ---------------------------------------------------------------------------
// Storey calculation context — single-pass computation
// ---------------------------------------------------------------------------

/**
 * Compute per-stairwell-ref GFA values used for both total stairwell GFA and distribution.
 * Returns [perRefGfaMap, totalApartmentsPerStorey, totalGfaPerStoreyAcrossStairwells].
 */
const computeStairwellPerRefInputs = (
  design: BuildingDesign,
  storey: Storey,
): {
  perRefGfa: Map<ActivityReference['id'], number>;
  totalApartmentsPerStorey: number;
  totalGfaPerStoreyAcrossStairwells: number;
} => {
  const stairwellRefs = storey.activities.filter(
    (ref) => ref.type === ActivityType.Stairwells,
  );

  const perRefGfa = new Map<ActivityReference['id'], number>();
  let totalApartmentsPerStorey = 0;
  let totalGfaPerStoreyAcrossStairwells = 0;

  if (stairwellRefs.length === 0) {
    return {
      perRefGfa,
      totalApartmentsPerStorey,
      totalGfaPerStoreyAcrossStairwells,
    };
  }

  const gfaOpenings =
    CALCULATIONS.gfa_openings_per_stairwell_per_storey.calculate();

  for (const ref of stairwellRefs) {
    const activity = getActivityByReference(design, ref);
    const properties = getStairwellsPropertiesWithDefaults(
      activity?.type === ActivityType.Stairwells
        ? activity.properties
        : undefined,
    );

    const elevatorsPerStairwell =
      properties.elevators_per_stairwell ??
      expressionVariablesConstants.elevator_count_per_stairwell;

    const apartmentsPerStairwellPerStorey =
      properties.apartments_per_stairwell_per_storey ??
      CALCULATIONS.stairwells_apartments_per_stairwell_per_storey.calculate();

    const gfaElevator =
      expressionVariablesConstants.gfa_per_elevator_per_storey *
      elevatorsPerStairwell;

    const gfaFloor = CALCULATIONS.gfa_floor_per_stairwell_per_storey.calculate({
      stairwells_apartments_per_stairwell_per_storey:
        apartmentsPerStairwellPerStorey,
    });

    const gfaPerStairwell = CALCULATIONS.gfa_per_stairwell_per_storey.calculate(
      {
        gfa_elevator_per_stairwell_per_storey: gfaElevator,
        gfa_openings_per_stairwell_per_storey: gfaOpenings,
        gfa_floor_per_stairwell_per_storey: gfaFloor,
      },
    );

    perRefGfa.set(ref.id, gfaPerStairwell);
    totalApartmentsPerStorey += apartmentsPerStairwellPerStorey;
    totalGfaPerStoreyAcrossStairwells += gfaPerStairwell;
  }

  return {
    perRefGfa,
    totalApartmentsPerStorey,
    totalGfaPerStoreyAcrossStairwells,
  };
};

/**
 * Compute total stairwell GFA on a storey from geometry and aggregated per-ref inputs.
 */
const computeTotalStairwellsGfa = (
  gfa: number,
  gfaFacades: number,
  gfaInstallations: number,
  residentialProperties: ActivityPropertyMap[ActivityType.Residential],
  totalApartmentsPerStorey: number,
  totalGfaPerStoreyAcrossStairwells: number,
): number => {
  if (gfa <= 0 || totalGfaPerStoreyAcrossStairwells <= 0) return 0;

  const gfaStairwellPerLivingArea =
    CALCULATIONS.gfa_stairwell_per_living_area.calculate({
      stairwells_apartments_per_stairwell_per_storey: totalApartmentsPerStorey,
      apartments_living_area_per_apartment:
        residentialProperties.living_area_per_apartment,
      gfa_per_stairwell_per_storey: totalGfaPerStoreyAcrossStairwells,
    });

  const gfaStairwellPerGfaApartment =
    CALCULATIONS.gfa_stairwell_per_gfa_apartment.calculate({
      gfa_stairwell_per_living_area: gfaStairwellPerLivingArea,
    });

  const gfaApartmentPlusStairwell =
    CALCULATIONS.gfa_apartment_plus_stairwell.calculate({
      gfa_activities: gfa,
      gfa_facades: gfaFacades,
      gfa_installations: gfaInstallations,
      garage_gfa: 0,
      gfa_other_activities: 0,
    });

  const gfaApartments = CALCULATIONS.gfa_apartments.calculate({
    gfa_apartment_plus_stairwell: gfaApartmentPlusStairwell,
    gfa_stairwell_per_gfa_apartment: gfaStairwellPerGfaApartment,
  });

  return CALCULATIONS.gfa_stairwells.calculate({
    gfa_apartment_plus_stairwell: gfaApartmentPlusStairwell,
    gfa_apartments: gfaApartments,
  });
};

/**
 * Compute total innerwalls GFA on a storey.
 * Purely derived from residential/stairwell properties — no user-editable innerwall properties.
 * Uses pre-computed stairwell GFA to avoid the recursive dependency.
 */
const computeTotalInnerwallsGfa = (
  storey: Storey,
  gfa: number,
  gfaFacades: number,
  gfaInstallations: number,
  gfaStairwells: number,
  residentialProperties: ActivityPropertyMap[ActivityType.Residential],
  stairwellsProperties: ActivityPropertyMap[ActivityType.Stairwells],
): number => {
  const hasInnerwalls = storey.activities.some(
    (ref) => ref.type === ActivityType.Innerwalls,
  );
  if (!hasInnerwalls || gfa <= 0) return 0;

  const distributableGfa = Math.max(
    0,
    gfa - gfaFacades - gfaInstallations - gfaStairwells,
  );

  const residentialShare = storey.activities.reduce((acc, ref) => {
    if (ref.type !== ActivityType.Residential) return acc;
    return acc + (ref.share ?? 0);
  }, 0);

  const livingAreaPerApartment =
    residentialProperties.living_area_per_apartment;
  if (livingAreaPerApartment <= 0) return 0;

  const laApartments =
    distributableGfa *
    residentialShare *
    expressionVariablesConstants.la_apartment_per_gfa_apartment;

  const apartmentCount = CALCULATIONS.apartment_count.calculate({
    apartments_living_area_per_apartment: livingAreaPerApartment,
    la_apartments: laApartments,
  });

  // Derive stairwell count from the pre-computed stairwell GFA
  const gfaOpenings =
    CALCULATIONS.gfa_openings_per_stairwell_per_storey.calculate();
  const gfaElevator =
    expressionVariablesConstants.gfa_per_elevator_per_storey *
    stairwellsProperties.elevators_per_stairwell;
  const gfaFloor = CALCULATIONS.gfa_floor_per_stairwell_per_storey.calculate({
    stairwells_apartments_per_stairwell_per_storey:
      stairwellsProperties.apartments_per_stairwell_per_storey,
  });
  const gfaPerStairwell = CALCULATIONS.gfa_per_stairwell_per_storey.calculate({
    gfa_openings_per_stairwell_per_storey: gfaOpenings,
    gfa_elevator_per_stairwell_per_storey: gfaElevator,
    gfa_floor_per_stairwell_per_storey: gfaFloor,
  });
  const stairwellCount =
    gfaPerStairwell > 0 ? gfaStairwells / gfaPerStairwell : 0;

  return (
    CALCULATIONS.gfa_apartment_parting_internal_walls.calculate({
      apartments_living_area_per_apartment: livingAreaPerApartment,
      apartment_count: apartmentCount,
    }) +
    CALCULATIONS.gfa_stairwell_parting_walls.calculate({
      gfa_stairwells: gfaStairwells,
    }) +
    CALCULATIONS.gfa_elevator_shaft_walls.calculate({
      stairwell_count: stairwellCount,
      gfa_elevator_per_stairwell_per_storey: gfaElevator,
    })
  );
};

/**
 * Build a {@link StoreyCalculationContext} for a storey.
 * Computes all intermediate values exactly once in dependency order:
 * geometry → facades/installations → stairwells → innerwalls.
 * @param design Building design
 * @param storey Storey to compute context for
 */
export const calculateStoreyContext = (
  design: BuildingDesign,
  storey: Storey,
): StoreyCalculationContext => {
  const gfa = getGfaFromStoreys(storey);
  const rawPerimeter = !storey.mezzanine
    ? getPerimeterFromStoreys([storey])
    : 0;
  const envelope = getStoreyEnvelopeMetricsById(design)[storey.id];
  const sharedWallLength = getSharedLengthForPerimeter(
    envelope,
    storey.height ?? 0,
  );
  const perimeter = Math.max(0, rawPerimeter - sharedWallLength);

  // Raw geometry-derived values used internally by stairwell/innerwall formulas
  const rawGfaFacades = CALCULATIONS.gfa_facades.calculate({ perimeter });
  const rawGfaInstallations = CALCULATIONS.gfa_installations.calculate({ gfa });

  // Only expose calculated areas for types that actually have refs on this storey
  const hasType = (t: ActivityType) =>
    storey.activities.some((ref) => ref.type === t);
  const gfaFacades = hasType(ActivityType.Facades) ? rawGfaFacades : 0;
  const gfaInstallations = hasType(ActivityType.Installations)
    ? rawGfaInstallations
    : 0;

  const residentialProperties: ActivityPropertyMap[ActivityType.Residential] = {
    ...defaultResidentialProperties,
    ...(getActivityByType(design, ActivityType.Residential)?.properties ?? {}),
  };

  const stairwellsProperties = getStairwellsPropertiesWithDefaults(
    getActivityByType(design, ActivityType.Stairwells)?.properties,
  );

  // 1. Stairwells (depends on geometry + properties only)
  const {
    perRefGfa,
    totalApartmentsPerStorey,
    totalGfaPerStoreyAcrossStairwells,
  } = computeStairwellPerRefInputs(design, storey);

  // Internal formulas always use raw geometry values (not conditioned on ref presence)
  const gfaStairwells = computeTotalStairwellsGfa(
    gfa,
    rawGfaFacades,
    rawGfaInstallations,
    residentialProperties,
    totalApartmentsPerStorey,
    totalGfaPerStoreyAcrossStairwells,
  );

  // 2. Innerwalls (depends on stairwells result, derived from residential/stairwell properties)
  const gfaInnerwalls = computeTotalInnerwallsGfa(
    storey,
    gfa,
    rawGfaFacades,
    rawGfaInstallations,
    gfaStairwells,
    residentialProperties,
    stairwellsProperties,
  );

  return {
    gfa,
    perimeter,
    gfaFacades,
    gfaInstallations,
    gfaStairwells,
    gfaInnerwalls,
    residentialProperties,
    stairwellsProperties,
    stairwellPerRefGfa: perRefGfa,
  };
};
